/**
 * BMR (Basal Metabolic Rate) calculation utilities
 * 
 * Provides pure functions for calculating BMR using different formulas
 * and applying activity level multipliers.
 */

// ========================================
// Types
// ========================================

import type { Gender, ActivityLevel, BmrFormula } from '@macro-tracker/models';
export type { Gender, ActivityLevel, BmrFormula };

export interface BmrInput {
  weight: number; // kg
  height: number; // cm
  age: number; // years
  gender: Gender;
  activityLevel: ActivityLevel;
  formula: BmrFormula;
  highMuscleContent: boolean;
}

export interface BmrResult {
  bmr: number; // calories per day
  tdee: number; // total daily energy expenditure
  formula: BmrFormula;
  activityLevel: ActivityLevel;
  highMuscleContent: boolean;
}

// ========================================
// Constants
// ========================================

/** Activity level multipliers (Physical Activity Level - PAL) */
export const ACTIVITY_MULTIPLIERS: Record<ActivityLevel, number> = {
  'sedentary': 1.2,
  'lightly-active': 1.375,
  'moderately-active': 1.55,
  'very-active': 1.725,
  'extra-active': 1.9,
} as const;

/** Activity level descriptions */
export const ACTIVITY_DESCRIPTIONS: Record<ActivityLevel, string> = {
  'sedentary': 'Desk job, little to no structured exercise.',
  'lightly-active': 'Light exercise or sports 1–3 days per week.',
  'moderately-active': 'Moderate exercise 3–5 days per week.',
  'very-active': 'Hard exercise or sports 6–7 days per week.',
  'extra-active': 'Professional athlete or physically demanding job.',
} as const;

// ========================================
// Core BMR Calculation Functions
// ========================================

/**
 * Calculates BMR using the Obese formula
 * 
 * Males: (10 × weight) + (6.25 × height) - (5 × age) + 5
 * Females: (10 × weight) + (6.25 × height) - (5 × age) - 161
 * 
 * @param input - BMR calculation parameters
 * @returns BMR value in calories per day
 */
export const calculateObeseBmr = (input: Pick<BmrInput, 'weight' | 'height' | 'age' | 'gender'>): number => {
  const { weight, height, age, gender } = input;
  
  const baseCalculation = (10 * weight) + (6.25 * height) - (5 * age);
  const genderAdjustment = gender === 'male' ? 5 : -161;
  
  return baseCalculation + genderAdjustment;
};

/**
 * Calculates BMR using the Revised Harris-Benedict formula
 * 
 * Males: (13.397 × weight) + (4.799 × height) - (5.677 × age) + 88.362
 * Females: (9.247 × weight) + (3.098 × height) - (4.33 × age) + 447.593
 * 
 * @param input - BMR calculation parameters
 * @returns BMR value in calories per day
 */
export const calculateRevisedBmr = (input: Pick<BmrInput, 'weight' | 'height' | 'age' | 'gender'>): number => {
  const { weight, height, age, gender } = input;
  
  if (gender === 'male') {
    return (13.397 * weight) + (4.799 * height) - (5.677 * age) + 88.362;
  } else {
    return (9.247 * weight) + (3.098 * height) - (4.33 * age) + 447.593;
  }
};

// ========================================
// Main BMR Calculation Function
// ========================================

/**
 * Calculates BMR and TDEE (Total Daily Energy Expenditure)
 * 
 * @param input - Complete BMR calculation parameters
 * @returns Complete BMR calculation result
 */
export const calculateBmr = (input: BmrInput): BmrResult => {
  // Validate input
  validateBmrInput(input);
  
  // Calculate base BMR using selected formula
  const bmr = input.formula === 'obese' 
    ? calculateObeseBmr(input)
    : calculateRevisedBmr(input);
  
  // Calculate TDEE by applying activity multiplier
  const activityMultiplier = ACTIVITY_MULTIPLIERS[input.activityLevel];
  const tdee = Math.round(bmr * activityMultiplier);
  
  return {
    bmr: Math.round(bmr),
    tdee,
    formula: input.formula,
    activityLevel: input.activityLevel,
    highMuscleContent: input.highMuscleContent,
  };
};

// ========================================
// Utility Functions
// ========================================

/**
 * Validates BMR input parameters
 * 
 * @param input - BMR calculation parameters
 * @throws Error if validation fails
 */
export const validateBmrInput = (input: BmrInput): void => {
  const errors: string[] = [];
  
  if (input.weight <= 0 || input.weight > 1000) {
    errors.push('Weight must be between 0 and 1000 kg');
  }
  
  if (input.height <= 0 || input.height > 300) {
    errors.push('Height must be between 0 and 300 cm');
  }
  
  if (input.age < 0 || input.age > 150) {
    errors.push('Age must be between 0 and 150 years');
  }
  
  if (!['male', 'female'].includes(input.gender)) {
    errors.push('Gender must be either "male" or "female"');
  }
  
  if (!Object.keys(ACTIVITY_MULTIPLIERS).includes(input.activityLevel)) {
    errors.push('Invalid activity level');
  }
  
  if (!['obese', 'revised'].includes(input.formula)) {
    errors.push('Formula must be either "obese" or "revised"');
  }
  
  if (errors.length > 0) {
    throw new Error(`BMR validation failed: ${errors.join(', ')}`);
  }
};

/**
 * Gets activity level options for UI display
 * 
 * @returns Array of activity level options with values and descriptions
 */
export const getActivityLevelOptions = (): Array<{
  value: ActivityLevel;
  label: string;
  description: string;
}> => {
  return Object.entries(ACTIVITY_DESCRIPTIONS).map(([value, description]) => ({
    value: value as ActivityLevel,
    label: formatActivityLevelLabel(value as ActivityLevel),
    description,
  }));
};

/**
 * Formats activity level value for display
 * 
 * @param activityLevel - Activity level value
 * @returns Formatted label
 */
export const formatActivityLevelLabel = (activityLevel: ActivityLevel): string => {
  const labels: Record<ActivityLevel, string> = {
    'sedentary': 'Sedentary',
    'lightly-active': 'Lightly Active',
    'moderately-active': 'Moderately Active',
    'very-active': 'Very Active',
    'extra-active': 'Extra Active',
  };
  
  return labels[activityLevel];
};

/**
 * Converts weight from pounds to kilograms
 * 
 * @param pounds - Weight in pounds
 * @returns Weight in kilograms
 */
export const poundsToKilograms = (pounds: number): number => {
  return pounds / 2.20462;
};

/**
 * Converts weight from kilograms to pounds
 * 
 * @param kilograms - Weight in kilograms
 * @returns Weight in pounds
 */
export const kilogramsToPounds = (kilograms: number): number => {
  return kilograms * 2.20462;
};

/**
 * Converts height from inches to centimeters
 * 
 * @param inches - Height in inches
 * @returns Height in centimeters
 */
export const inchesToCentimeters = (inches: number): number => {
  return inches * 2.54;
};

/**
 * Converts height from centimeters to inches
 * 
 * @param centimeters - Height in centimeters
 * @returns Height in inches
 */
export const centimetersToInches = (centimeters: number): number => {
  return centimeters / 2.54;
};